import React, { useState, useEffect } from "react";
import "./index.scss";
import "./index-v2.scss";
import { useDebounce, omitNation } from "../../helpers";
import { getPlayers } from "../../services/players";
import { getPlayerDetails } from "../../services/h2h";
import Dropdown from "../../static/Dropdown/Dropdown";
import { getPlayerWinLoss } from "../../services/h2h";
import { getHexColor } from "../../helpers";

const playerMock = {
  FullName: "",
  playerNationalityCode: "N/A",
  playerNationality: "",
  familyName: "",
  givenName: "",
  profileImage: "",
  playerProfileLink: "",
  value: "",
  profileDefaultImage: "",
  playedCircuits: [],
  playedCategoryCodes: [],
  playerId: 0
};

interface DropdownItem {
  label: string;
  value: string;
  active: boolean;
}

interface Opponent {
  FullName: string;
  playerNationalityCode: string;
  playerNationality: string;
  familyName: string;
  givenName: string;
  profileImage: string;
  playerProfileLink: string;
  playerId: number;
  profileDefaultImage: string;
  playedCategoryCodes: DropdownItem[] | null;
  playedCircuits: DropdownItem[] | null;
  value: string;
}

interface player {
  playerId: number;
  playedCategoryCodes: string[];
}

interface WinLoss {
  overall: {
    losses: number;
    wins: number;
    winRate: number;
  };
  surfaces: surfaceType[];
}

interface surfaceType {
  name: string;
}

const EmptyCircuit = {
  label: "",
  value: "",
  active: false
};

const H2HModule = ({
  player1,
  player2,
  backgroundColor1,
  backgroundColor2
}: {
  player1?: player;
  player2: player;
  backgroundColor1?: string;
  backgroundColor2?: string;
}) => {
  let [searchPlayers, setSearchPlayers] = useState<{
    players: Opponent[];
    noResults?: boolean;
  }>({ players: [], noResults: false });
  let [timeout, updateTimeout] = useState(0);
  let [inputFocus, setInputFocus] = useState(0);
  let [isLoading, setIsloading] = useState(false);
  let [isPlayerLoading, setIsPlayerloading] = useState(false);
  let [currentCircuit, setCircuit] = useState<DropdownItem>(EmptyCircuit);

  let [winLoss, updateWinLoss] = useState<WinLoss>({
    overall: { wins: 0, losses: 0, winRate: 0 },
    surfaces: []
  });

  const [opponents, updateOpponents] = useState<Opponent[]>([
    {
      ...playerMock
    },
    {
      ...playerMock
    }
  ]);

  useEffect(() => {
    // Reset current circuit select when player one is changed
    setCircuit(EmptyCircuit);
    if (opponents[0].playedCircuits !== null) {
      if (opponents[0].playedCircuits.length === 1) {
        setCircuit(opponents[0].playedCircuits[0]);
      }
    }
  }, [opponents[0].FullName]);

  useEffect(() => {
    if (currentCircuit) {
      getPlayerWinLoss(
        opponents[0].playerId,
        opponents[1].playerId,
        currentCircuit.value
      ).then((res: WinLoss) => {
        updateWinLoss(res);
      });
    }
  }, [currentCircuit, opponents[1].FullName]);

  useEffect(() => {
    setSearchPlayers({ players: [], noResults: false });
  }, [inputFocus]);

  let playerIds = [player1, player2].filter(
    player => player && player.playerId
  );

  useEffect(() => {
    if (playerIds) {
      setIsPlayerloading(true);

      let promiseChain = playerIds.map(player => {
        if (player && player.playerId) {
          return getPlayerDetails(
            player.playerId,
            player.playedCategoryCodes[0]
          );
        }
        return;
      });

      promiseChain.push;

      Promise.all(promiseChain).then(res => {
        updateOpponents(res.length > 1 ? res : [...res, playerMock]);
        setIsPlayerloading(false);
        setCircuit(res[0].playedCategoryCodes[0]);
        getPlayerWinLoss(
          res[0].playerId,
          res[1].playerId,
          res[0].playedCategoryCodes[0].value
        ).then((res: WinLoss) => {
          updateWinLoss(res);
        });
      });
    }
  }, []);

  const onKeyPressHandler = (value: string) => {
    if (value === "") {
      setSearchPlayers({ players: [], noResults: false });
      return;
    } else {
      setIsloading(true);
      getPlayers(value, inputFocus === 0 ? "" : currentCircuit.value).then(
        res => {
          setSearchPlayers({
            players: res.players.filter((p: Opponent) =>
              opponents.every((op: Opponent) => op.playerId !== p.playerId)
            ),
            noResults: res.players.length ? false : true
          });
          setIsloading(false);
        }
      );
    }
  };

  const Results = ({
    noResults,
    inputValueUpdate
  }: {
    noResults?: boolean;
    inputValueUpdate: Function;
  }) => {
    return searchPlayers.players.length ? (
      <div className="search-results">
        {searchPlayers.players.length ? (
          searchPlayers.players.map((player: Opponent, key: number) => (
            <div
              key={key}
              className="search-results__player"
              onClick={() => {
                inputValueUpdate(``, player);
                setSearchPlayers({ players: [] });
              }}
            >
              {`${player.FullName} ${
                omitNation(player.playerNationalityCode)!!
                  ? `[${player.playerNationalityCode}]`
                  : ""
              }`}
            </div>
          ))
        ) : noResults ? (
          <div className="search-results__player">No results</div>
        ) : null}
      </div>
    ) : null;
  };

  const schemaColors: Record<string, string> = {
    "--h2h-bg-color-start": backgroundColor1
      ? getHexColor(backgroundColor1)
      : "",
    "--h2h-bg-color-end": backgroundColor2 ? getHexColor(backgroundColor2) : ""
  };

  return (
    <section className="page-section" style={schemaColors}>
      <div className="page-section__inner">
        <div className="H2H-module">
          <div className="container">
            <h2>Head to Head</h2>
            <div className="H2H-module__players">
              {opponents.map((opponent: Opponent, index: number) => (
                <div
                  key={index}
                  className={`H2H-module__player opponent-${index}`}
                >
                  <div
                    className={`H2H-module__player-search ${
                      isLoading && inputFocus === index ? "isLoading" : ""
                    }`}
                  >
                    <input
                      placeholder={`Search`}
                      value={opponent.value}
                      onFocus={() => setInputFocus(index)}
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>
                      ): void => {
                        updateOpponents(
                          opponents.map((op: Opponent, id: number) => ({
                            ...op,
                            value: id === index ? e.target.value : op.value
                          }))
                        );
                        useDebounce({
                          timeout,
                          updateTimeout,
                          delay: 500,
                          func: onKeyPressHandler,
                          params: [e.target.value, index]
                        });
                      }}
                    />
                    {inputFocus === index ? (
                      <Results
                        inputValueUpdate={(
                          value: string,
                          otherProps?: object
                        ) =>
                          updateOpponents(
                            opponents.map((op: Opponent, id: number) => ({
                              ...opponent,
                              ...(id === index ? otherProps : op),
                              value: id === index ? value : op.value
                            }))
                          )
                        }
                        noResults={searchPlayers.noResults}
                      />
                    ) : null}
                  </div>
                  {index === 0 &&
                  !currentCircuit.value &&
                  opponent.playedCircuits &&
                  opponent.playedCircuits.length > 1 ? (
                    <Dropdown
                      className="H2H-module__tour-dropdown--mobile"
                      onChange={(e: DropdownItem) => {
                        setCircuit(e);
                      }}
                      placeholder={`Select Tour`}
                      options={
                        opponent.playedCircuits || opponent.playedCategoryCodes
                      }
                    />
                  ) : null}
                  {index === 0 &&
                  !currentCircuit.value &&
                  opponent.playedCircuits &&
                  opponent.playedCircuits.length > 1 ? (
                    <Dropdown
                      className="H2H-module__tour-dropdown"
                      onChange={(e: DropdownItem) => {
                        setCircuit(e);
                        getPlayerWinLoss(
                          opponents[0].playerId,
                          opponents[1].playerId,
                          e.value
                        ).then((res: WinLoss) => {
                          updateWinLoss(res);
                        });
                      }}
                      placeholder={`Select Tour`}
                      options={
                        opponent.playedCircuits || opponent.playedCategoryCodes
                      }
                    />
                  ) : (
                    <div />
                  )}
                  <a
                    href={opponent.playerProfileLink}
                    className="H2H-module__player-headshot"
                    style={{
                      backgroundImage: `url("${
                        process.env.HOST
                      }${opponent.profileImage ||
                        opponent.profileDefaultImage}")`
                    }}
                  ></a>
                  <div
                    className={`H2H-module__player-details${
                      isPlayerLoading ? " loading" : ""
                    }`}
                  >
                    {isPlayerLoading ? (
                      <div />
                    ) : (
                      <div className="H2H-module__player-details-container">
                        {opponent.familyName ? (
                          <>
                            <a
                              href={opponent.playerProfileLink}
                              className="H2H-module__player-name"
                            >
                              <div className="first">{opponent.givenName}</div>
                              <div className="last">{opponent.familyName}</div>
                            </a>
                            <div className="H2H-module__player-nationality">
                              <span
                                className={`itf-flags itf-flags--${opponent.playerNationalityCode}`}
                              />
                              <p className="long">{`${omitNation(
                                opponent.playerNationality
                              )}`}</p>
                              <p className="short">{`${omitNation(
                                opponent.playerNationalityCode
                              )}`}</p>
                            </div>
                          </>
                        ) : (
                          <p>No player selected</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div
              className={`H2H-module__compare${
                isPlayerLoading ? " isLoading" : ""
              }${!winLoss.overall.winRate ? " no-data" : ""}`}
            >
              <div className="H2H-module__player-winloss">
                <div className="H2H-module__player-winloss-value">
                  {winLoss.overall.wins}
                </div>
                <div className="H2H-module__player-winloss-bar">
                  <div
                    className="H2H-module__player-winloss-bar-rate"
                    style={{ width: winLoss.overall.winRate + "%" }}
                  />
                </div>
                <div className="H2H-module__player-winloss-value">
                  {winLoss.overall.losses}
                </div>
              </div>
              <a
                href={`/${
                  location.pathname.split("/").filter(p => p)[0]
                }/head-to-head/?circuitCode=${currentCircuit.value}&player1Id=${
                  opponents[0].playerId
                }${
                  opponents[1].playerId
                    ? `&player2Id=${opponents[1].playerId}`
                    : ""
                }`}
                className="H2H-module__compare-btn"
              >
                Compare Players
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default H2HModule;
